import React, { useState } from "react";
import { StyleSheet, View, Pressable } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { useAppConfig } from "../../../AppConfigProvider";

interface TabData {
  key: string;
  title: string;
  icon?: React.ReactNode;
}

interface AdminTabNavigatorProps {
  tabData: TabData[];
  activeTab?: string;
  onTabPress?: (tabKey: string) => void;
  children?: React.ReactNode | ((activeTab: string) => React.ReactNode);
}

const AdminTabNavigator: React.FC<AdminTabNavigatorProps> = ({
  tabData,
  activeTab: controlledActiveTab,
  onTabPress,
  children,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<string>(tabData[0]?.key || "");
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const appConfig = useAppConfig();

  // Use controlled activeTab if provided, otherwise use internal state
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  const handleTabPress = (tabKey: string) => {
    if (onTabPress) {
      onTabPress(tabKey);
    } else {
      setInternalActiveTab(tabKey);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.navBar}>
        {tabData.map((tab) => {
          const isActive = activeTab === tab.key;
          const isHovered = hoveredTab === tab.key;

          return (
            <Pressable
              key={tab.key}
              style={[styles.tab, isActive && styles.activeTab, isHovered && !isActive && styles.hoveredTab]}
              onPress={() => handleTabPress(tab.key)}
              onHoverIn={() => setHoveredTab(tab.key)}
              onHoverOut={() => setHoveredTab(null)}>
              {tab.icon && (
                <View style={styles.iconContainer}>
                  {React.cloneElement(tab.icon as React.ReactElement, {
                    color: isActive ? "#1E40AF" : "#667085",
                  })}
                </View>
              )}
              <CustomText
                text={tab.title}
                size="s"
                weight="medium"
                useTranslationText={false}
                style={[styles.tabText, isActive && styles.activeTabText]}
              />
            </Pressable>
          );
        })}
      </View>

      {children && (
        <View style={styles.contentContainer}>{typeof children === "function" ? children(activeTab) : children}</View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  navBar: {
    flexDirection: "row",
    gap: 4,
    padding: 4,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    alignSelf: "flex-start",
    marginBottom: 24,
  },
  tab: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: "transparent",
  },
  activeTab: {
    backgroundColor: "#CCD7FF",
  },
  hoveredTab: {
    backgroundColor: "#F2F4F7",
  },
  iconContainer: {
    width: 16,
    height: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  tabText: {
    fontSize: 14,
    color: "#667085",
  },
  activeTabText: {
    color: "#1E40AF",
  },
  contentContainer: {
    flex: 1,
  },
});

export default AdminTabNavigator;
